/**
 * @file AssetsRow.js
 * @description Fluid grid of plan asset tiles.
 */

import styles from './AssetsRow.module.scss';
import FullscreenDisplay from '../../assets/shared/FullscreenDisplay';
import { useContext, useState } from 'react';
import VideoThumbnail from '../../ui/VideoThumbnail';
import Toast from '../../ui/toasts/Toast';
import { getFileIcon } from '../../ui/svg/FileIcons';
import {
    canRenderAssetPreviewInBrowser,
    getFileCategory,
    processFileName,
} from '../../helpers/FileUtils';
import { ModalContext, ModalTypes } from '../../../store/ModalContext';
import { logEvent } from '../../../services/loggingService';
import { EventDataTypeEnum, LogEventType } from '../../../utils/data/enums';
import { getText } from '../../../i18n';

function isVideo(asset) {
    return getFileCategory(asset.fileType) === 'video';
}

function AssetTile({ asset, onOpenPreview }) {
    return (
        <button
            type="button"
            onClick={function handleClick() {
                onOpenPreview(asset);
            }}
            className={`d-flex align-items-center gap-3 border rounded-3 bg-white p-3 text-reset text-decoration-none h-100 ${styles.tile}`}
            aria-label={`Open preview for ${asset.name || 'asset'}`}
        >
            <div
                className={`flex-shrink-0 d-flex align-items-center justify-content-center ${styles.iconSlot}`}
            >
                {isVideo(asset) ? (
                    <VideoThumbnail
                        videoUrl={asset.url}
                        avatarURL={asset.avatarURL}
                        width={80}
                        height={56}
                        alt={asset.name || 'Video thumbnail'}
                    />
                ) : (
                    getFileIcon(asset.fileType)
                )}
            </div>
            <span className={`fw-semibold ${styles.title}`}>
                {asset.name || 'Untitled asset'}
            </span>
        </button>
    );
}

export default function AssetsRow({
    assets,
    assetDetailsMap,
    onAssetOpen,
    planId = null,
    contextName = null,
}) {
    const { showModal } = useContext(ModalContext);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fullscreenImageUrl, setFullscreenImageUrl] = useState('');
    const [fullscreenMediaType, setFullscreenMediaType] = useState('image');

    if (!assets || assets.length === 0) {
        return null;
    }

    function handleDownloadAsset(asset) {
        void logEvent(
            LogEventType.UserDocumentDownloaded,
            `Asset downloaded: ${asset?.name || 'Untitled asset'}`,
            {
                subjectDataTypeId: EventDataTypeEnum.Asset,
                subjectId: asset?.id ?? null,
                subjectName: asset?.name || 'Untitled asset',
                contextDataTypeId: EventDataTypeEnum.Benefits,
                contextId: planId,
                contextName,
            },
        );

        if (!asset?.url) {
            Toast.error(getText('msg_02e2454dea84'));
            return;
        }

        const processedName = processFileName(asset.name, asset.fileType);

        fetch(asset.url)
            .then(function handleResponse(response) {
                if (!response.ok) {
                    throw new Error('Network response failed');
                }
                return response.blob();
            })
            .then(function handleBlob(blob) {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = processedName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            })
            .catch(function handleError(error) {
                console.error(
                    'There was a problem downloading the file:',
                    error,
                );
                Toast.error(getText('msg_e354a74956ef'));
            });
    }

    const openFullscreen = (mediaUrl, mediaType = 'image') => {
        setFullscreenImageUrl(mediaUrl);
        setFullscreenMediaType(mediaType);
        setIsFullscreen(true);
    };

    const closeFullscreen = () => {
        setIsFullscreen(false);
        setFullscreenImageUrl('');
        setFullscreenMediaType('image');
    };

    function handleOpenPreview(asset) {
        if (!asset?.url) {
            Toast.error(getText('msg_2f0f56e07290'));
            return;
        }

        if (onAssetOpen) {
            onAssetOpen(asset);
        }

        const assetCategory = getFileCategory(asset.fileType);
        const canRenderDocumentInBrowser =
            assetCategory === 'document' &&
            canRenderAssetPreviewInBrowser(
                asset.fileType,
                process.env.REACT_APP_DEPLOYED_ENV_NAME,
            );

        if (assetCategory === 'image') {
            openFullscreen(asset.url, 'image');
        } else if (assetCategory === 'video') {
            openFullscreen(asset.url, 'video');
        } else {
            showModal({
                type: ModalTypes.ASSET_VIEW,
                title: asset.name,
                text: asset.url,
                fileType: asset.fileType,
                download: function onDownload() {
                    handleDownloadAsset(asset);
                },
            });
        }
    }

    const tiles = assets
        .map(function lookup(a) {
            return assetDetailsMap?.[a.assetId];
        })
        .filter(function ready(d) {
            return d && d.url;
        });

    if (tiles.length === 0) {
        return null;
    }

    return (
        <>
            <FullscreenDisplay
                isFullscreen={isFullscreen}
                fullscreenImageUrl={fullscreenImageUrl}
                mediaType={fullscreenMediaType}
                closeFullscreen={closeFullscreen}
            />
            <div className={styles.grid}>
                {tiles.map(function renderTile(detail) {
                    return (
                        <AssetTile
                            key={detail.id}
                            asset={detail}
                            onOpenPreview={handleOpenPreview}
                        />
                    );
                })}
            </div>
        </>
    );
}
